import { deleteRequest, getRequest } from '@/utils/data-fetching/config.ts'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'

export const urls = {
  recentHistory: '/history/recent',
  history: '/history',
}

export interface HistoryItem {
  id: string
  queryId: string
  userId: string
  query: string
  queryTime: string
}

export interface HistoryResponse {
  items: HistoryItem[]
  total: number
}

export interface GetHistoryParams {
  from: number
  size: number
  term?: string
}

export async function getRecentHistory(): Promise<HistoryResponse> {
  const response = await getRequest<HistoryResponse>('/history/recent')
  return response.data
}

export async function getHistory(
  params: GetHistoryParams,
): Promise<HistoryResponse> {
  if (!params.term) {
    delete params.term
  }

  const response = await getRequest<HistoryResponse>('/history', { params })
  return response.data
}

export interface DeleteHistoryParams {
  ids?: string[]
  all?: boolean
  startAt?: string
  endAt?: string
}

export async function deleteHistoryItems(data: DeleteHistoryParams) {
  return deleteRequest('/history', { data })
}

export interface UseRecentHistoryParams {
  isAuthenticated: boolean
}
export const useRecentHistory = ({
  isAuthenticated,
}: UseRecentHistoryParams): UseQueryResult<HistoryResponse> =>
  useQuery({
    queryKey: ['history', 'recent'],
    queryFn: getRecentHistory,
    enabled: isAuthenticated,
  })

export interface UsePageHistoryParams {
  currentPage: number
  searchTerm: string
  isAuthenticated: boolean
}
export interface UsePageHistoryResponse {
  data: HistoryResponse
  isLoading: boolean
}
export const usePageHistory = ({
  currentPage,
  searchTerm,
  isAuthenticated,
}: UsePageHistoryParams): UseQueryResult<HistoryResponse> =>
  useQuery({
    queryKey: ['history', currentPage, searchTerm],
    queryFn: getRecentHistory,
    enabled: isAuthenticated,
  })

export const useDeleteHistoryMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteHistoryItems,
    onSuccess: () => {
      // Slight delay on backend when is updated in opensearch
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['history'] })
        void queryClient.invalidateQueries({ queryKey: ['history', 0, ''] })
      }, 500)
    },
  })
}
